import request from '@utils/request';
import moment, { Moment } from 'moment';
import type { Dispatch } from 'redux';
import { JourneyParams } from 'shared-types/Common';
import {
  buildCoverageKey,
  monthWindow,
  planCoverage,
  type DateRange,
} from './availabilityCoverage';

const PREFIX = '[ROOMS]';

const MONTHS_PER_WINDOW = 3;

type CoverageSession = {
  key: string;
  covered: DateRange | null;
  inFlight: Map<string, Promise<void>>;
};

let session: CoverageSession | null = null;

export const resetRoomRateAvailabilityCoverage = () => {
  session = null;
};

export const FETCH_ROOM_RATE_AVAILABILITY_REQUEST = `${PREFIX} FETCH_ROOM_RATE_AVAILABILITY_REQUEST`;
export const FETCH_ROOM_RATE_AVAILABILITY_SUCCESS = `${PREFIX} FETCH_ROOM_RATE_AVAILABILITY_SUCCESS`;
export const FETCH_ROOM_RATE_AVAILABILITY_FAILURE = `${PREFIX} FETCH_ROOM_RATE_AVAILABILITY_FAILURE`;
export const FETCH_ROOM_RATE_AVAILABILITY_RESET = `${PREFIX} FETCH_ROOM_RATE_AVAILABILITY_RESET`;

export const resetRoomRateAvailability = {
  type: FETCH_ROOM_RATE_AVAILABILITY_RESET,
};

export const fetchRoomRateAvailabilityRequest = {
  type: FETCH_ROOM_RATE_AVAILABILITY_REQUEST,
};

export const fetchRoomRateAvailabilitySuccess = (data) => ({
  type: FETCH_ROOM_RATE_AVAILABILITY_SUCCESS,
  payload: data,
});

export const fetchRoomRateAvailabilityFailure = (data) => ({
  type: FETCH_ROOM_RATE_AVAILABILITY_FAILURE,
  payload: data,
});

export const fetchRoomRateAvailability =
  (dispatch: Dispatch) =>
  (
    propertyId: number,
    {
      fromDate = moment(new Date()),
      adults = 2,
      children = 0,
      promoRateCode,
      childAges = {},
    }: JourneyParams,
    extendedDate?: Moment,
    useRateCode = false,
    rateCode = '',
    roomCode = ''
  ) => {
    const requested = monthWindow({
      start: extendedDate ?? moment(fromDate),
      months: MONTHS_PER_WINDOW,
    });

    const childAgesValues = Object.values(childAges);

    const coverageKey = buildCoverageKey({
      propertyId,
      adults,
      children,
      childAges: childAgesValues,
      promoRateCode: useRateCode ? promoRateCode : undefined,
      rateCode,
      roomCode,
    });

    const previousSession = session;
    const current: CoverageSession =
      previousSession && previousSession.key === coverageKey
        ? previousSession
        : { key: coverageKey, covered: null, inFlight: new Map() };

    if (current !== previousSession) {
      if (previousSession) {
        dispatch(resetRoomRateAvailability);
      }
      session = current;
    }

    const plan = planCoverage({ requested, covered: current.covered });

    if (plan.status === 'covered') {
      return Promise.resolve();
    }

    const requestKey = `${plan.missing.start}|${plan.missing.end}`;
    const pending = current.inFlight.get(requestKey);

    if (pending) {
      return pending;
    }

    current.covered = plan.nextCovered;

    const childAgesParam = childAgesValues.length
      ? { 'child-ages': childAgesValues.join(',') }
      : undefined;

    dispatch(fetchRoomRateAvailabilityRequest);
    const url = `${propertyId}/rooms/calendar/${adults}/${plan.missing.start}/${plan.missing.end}/availability`;
    const params = {
      children,
      ...childAgesParam,
    };

    if (useRateCode && promoRateCode) {
      params['promo-rate-code'] = promoRateCode;
    }

    if (rateCode) {
      params['rate-code'] = rateCode;
    }

    if (roomCode) {
      params['room-code'] = roomCode;
    }

    const inFlight = request
      .get(url, { params })
      .then(
        ({ data }) => {
          if (session !== current) {
            return;
          }
          dispatch(fetchRoomRateAvailabilitySuccess(data));
        },
        (error) => {
          if (session !== current) {
            return;
          }
          current.covered = null;
          dispatch(fetchRoomRateAvailabilityFailure(error));
        }
      )
      .finally(() => {
        current.inFlight.delete(requestKey);
      });

    current.inFlight.set(requestKey, inFlight);

    return inFlight;
  };
